/**
 * Prepare and execute fade in animation for the given element.
 *
 * IMPORTANT: Keep in mind that this animation is based on the opacity so the
 * element will take the space in the DOM regardless of the visibility.
 *
 * @param {HTMLElement} element Target element.
 * @param {number} duration     Fade in duration in ms. Default is 1000.
 */
export function fadeIn(element, duration = 1000) {
    if (!element) {
        return;
    }

    const durationSeconds = duration / 1000;

    element.style.opacity = '0';
    element.style.transition = `opacity ${durationSeconds}s ease-in-out`;

    return {
        hide: () =>
            new Promise((resolve) => {
                element.style.opacity = '0';
                window.setTimeout(resolve, duration);
            }),
        show: () =>
            new Promise((resolve) => {
                element.style.opacity = '1';
                delete element.dataset.fadeinGroup;
                window.setTimeout(resolve, duration);
            }),
    };
}

/**
 * Execute fade in animation for multiple child elements of a given parent element, where each element that should be
 * animated must have `data-fadein-group="{index}"` attribute.
 *
 * All elements from the same group will be displayed at the same time. Execution order is from the lowest to the highest
 * group number.
 *
 * This function uses `fadeIn` function from the same utility file.
 *
 * @param {HTMLELement} parentElement The main element whose children may contain `data-fadein-group` attribute.
 * @param {number} duration           Fade in duration in ms. Default is 1000.
 */
export function fadeInGroups(parentElement, duration = 1000) {
    if (!parentElement) {
        return;
    }

    const fadeInElements = parentElement.querySelectorAll('[data-fadein-group]');

    // Key is a group number extracted from `[data-fadein-group]` attribute, value is an array of all elements in that group
    const fadeInGroups = {};

    Array.from(fadeInElements).forEach((element) => {
        const fadeInGroup = element.getAttribute('data-fadein-group');

        if (!fadeInGroup) {
            return;
        }

        const elementFadeIn = fadeIn(element, duration);

        if (Array.isArray(fadeInGroups[fadeInGroup])) {
            fadeInGroups[fadeInGroup].push(elementFadeIn);
        } else {
            fadeInGroups[fadeInGroup] = [elementFadeIn];
        }
    });

    const orderedFadeInGroups = Object.keys(fadeInGroups).sort();

    return {
        show: async () => {
            for (const activeGroup of orderedFadeInGroups) {
                const warmPromises = fadeInGroups[activeGroup].map((elementFadeIn) => elementFadeIn.show());
                await Promise.all(warmPromises);
            }
        },
    };
}
